import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../../Navbar";
import Footer from "../../Footer";
import { ImSpinner2 } from "react-icons/im";

const CentralKitchenPhysical = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { inventoryData, selectedOutlet, accessToken } = location.state || {};
    const token = localStorage.getItem("token") || "";
    const inputRefs = useRef([]);
    const [isLoading, setIsLoading] = useState(false);
    const [physicalItems, setPhysicalItems] = useState([]);
    const [itemCosts, setItemCosts] = useState({});
    const [costLoading, setCostLoading] = useState({});
    const [costErrors, setCostErrors] = useState({});

    useEffect(() => {
        if (accessToken !== "a7fK2pX9Zd") {
            navigate('/central-kitchen/inventory-calculator');
        }
    }, [accessToken, navigate]);

    useEffect(() => {
        if (inventoryData) {
            const saved =
                JSON.parse(
                    localStorage.getItem("savedCentralKitchenPhysical")
                ) || [];

            const items = Object.entries(inventoryData).map(
                ([name, item]) => {
                    const savedItem = saved.find(
                        (s) => s.item_name === name
                    );

                    return {
                        id: `${name}-${item.uom}`,

                        item_name: name,

                        category: item.type,

                        primaryQty: savedItem
                            ? String(savedItem.primaryQty)
                            : "",

                        secondaryQty: savedItem
                            ? String(savedItem.secondaryQty)
                            : "",

                        uom: item.uom,

                        new_uom: item.new_uom,

                        unitdefinition:
                            item.unitdefinition || {},

                        closing_balance:
                            item.closing_balance || 0
                    };
                }
            );

            setPhysicalItems(items);
        }
    }, [inventoryData]);

    const fetchPhysicalCost = async (item) => {
        try {
            const physicalCount = computeFinalQuantity(item, unitMappings);

            if (!physicalCount || physicalCount <= 0) {
                setItemCosts(prev => ({
                    ...prev,
                    [item.id]: 0
                }));

                return;
            }

            setCostLoading(prev => ({
                ...prev,
                [item.id]: true
            }));

            setCostErrors(prev => ({
                ...prev,
                [item.id]: null
            }));

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/calculate-physical-cost`,
                {
                    token: token,
                    item_name: item.item_name,
                    outlet: selectedOutlet,
                    costcenter: "Central Kitchen",
                    physical_count: physicalCount,
                    centralkitchen_production_flag: true
                }
            );

            const data = response.data || {};

            setItemCosts(prev => ({
                ...prev,
                [item.id]: data?.physical_cost || 0
            }));

        } catch (error) {
            console.error("Error calculating physical cost:", error);

            setCostErrors(prev => ({
                ...prev,
                [item.id]:
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    "Failed to calculate cost"
            }));

        } finally {
            setCostLoading(prev => ({
                ...prev,
                [item.id]: false
            }));
        }
    };

    const handlePrimaryChange = (id, value) => {
        setPhysicalItems(prevItems =>
            prevItems.map(item =>
                item.id === id ? {
                    ...item,
                    primaryQty: value === "" ? "" : value
                } : item
            )
        );
    };

    const handleSecondaryChange = (id, value) => {
        setPhysicalItems(prevItems =>
            prevItems.map(item =>
                item.id === id ? {
                    ...item,
                    secondaryQty: value === "" ? "" : value
                } : item
            )
        );
    };

    function computeFinalQuantity(item, unitMappings) {
        const primaryQty = parseFloat(item.primaryQty) || 0;
        const secondaryQty = parseFloat(item.secondaryQty) || 0;
        const unitSize = parseFloat(item.unitdefinition?.unit) || 1;
        const baseUom = (item.unitdefinition.uom || '').toLowerCase();
        const itemUom = (item.uom || '').toLowerCase();
        const newUom = (item.new_uom || '').toLowerCase();

        const {
            kgVariations,
            gramVariations,
            literVariations,
            milliliterVariations
        } = unitMappings;

        const isMatch = (uom, variations) => variations.includes(uom);

        let primaryInBaseUnit, secondaryInBaseUnit;

        if (itemUom === baseUom) {
            primaryInBaseUnit = primaryQty;
        } else {
            primaryInBaseUnit = primaryQty * unitSize;
        }

        if (newUom === baseUom) {
            secondaryInBaseUnit = secondaryQty;
        } else {
            if (isMatch(baseUom, kgVariations) && isMatch(newUom, gramVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else if (isMatch(baseUom, literVariations) && isMatch(newUom, milliliterVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else {
                secondaryInBaseUnit = secondaryQty;
            }
        }

        const totalInBaseUnit = (primaryInBaseUnit + secondaryInBaseUnit) / unitSize;

        // const totalInBaseUnit = (primaryInBaseUnit + secondaryInBaseUnit);

        // if (itemUom !== baseUom) {
        //     return (totalInBaseUnit / unitSize);
        // }

        return totalInBaseUnit;
    }

    const unitMappings = {
        kgVariations: ['kg', 'kgs', 'kilogram', 'kilograms'],
        gramVariations: ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'],
        literVariations: ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'],
        milliliterVariations: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls']
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this physical count?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!token || !selectedOutlet) {
                                toast.error("Please select an outlet");
                                return;
                            }

                            setIsLoading(true);
                            try {
                                const existing = JSON.parse(localStorage.getItem('savedCentralKitchenPhysical')) || [];

                                const updatedExisting = existing.filter(
                                    oldItem => !physicalItems.some(f => f.id === oldItem.id)
                                );

                                const newItems = physicalItems.map(item => ({
                                    id: item.id,
                                    item_name: item.item_name,
                                    quantity: computeFinalQuantity(item, unitMappings),
                                    primaryQty: item.primaryQty,
                                    secondaryQty: item.secondaryQty
                                }));

                                const finalData = [...updatedExisting, ...newItems];

                                localStorage.setItem('savedCentralKitchenPhysical', JSON.stringify(finalData));
                                toast.success("Physical count saved!");
                                navigate('/central-kitchen/inventory-calculator');
                            } catch (error) {
                                console.error('Error saving physical count locally:', error);
                                toast.error('Error saving physical count');
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-physical-stocks'
            }
        );
    };

    const shouldShowSecondary = (item) => {
        const itemUom = (item.uom || "")
            .toLowerCase()
            .trim();

        const newUom = (item.new_uom || "")
            .toLowerCase()
            .trim();

        const defUom = (
            item.unitdefinition?.uom || ""
        )
            .toLowerCase()
            .trim();

        if (!defUom) {
            return itemUom !== newUom;
        }

        return !(
            itemUom === newUom &&
            newUom === defUom
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();

            const currentIndex = inputRefs.current.indexOf(e.target);
            const nextInput = inputRefs.current[currentIndex + 1];

            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');

        const filtered = [...physicalItems].sort((a, b) => {
            return a.item_name.localeCompare(b.item_name);
        });

        const content = `
        <html>
        <head>
            <title>Stock Count Sheet</title>
            <style>
                body {
                    font-family: Arial, sans-serif;
                    padding: 5px;
                }
                .header {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 15px;
                    font-size: 14px;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }
                th, td {
                    border: 1px solid black;
                    padding: 8px;
                    text-align: center;
                }
                th {
                    background-color: #f2f2f2;
                }
                .qty-box {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 5px;
                }
                .line {
                    width: 60px;
                }
            </style>
        </head>
        <body>
            <div class="header">
                <div>Stock Count for Central Kitchen</div>
                <div>Date: ____________</div>
            </div>

            <table>
                <thead>
                    <tr>
                        <th>Item Name</th>
                        <th>Unit Definition</th>
                        <th>Primary</th>
                        <th>Secondary</th>
                    </tr>
                </thead>
                <tbody>
                    ${filtered.map(item => {
            const showSecondary = item.uom !== item.new_uom;

            return `
                            <tr>
                                <td>${item.item_name}</td>
                                <td>${item.unitdefinition?.unit || ''} (${item.unitdefinition?.uom || ''})</td>

                                <td>
                                    <div class="qty-box">
                                        <span class="line"></span>
                                        <span>${item.uom}</span>
                                    </div>
                                </td>

                                <td>
                                ${showSecondary
                    ? `
                                        <div class="qty-box">
                                            <span class="line"></span>
                                            <span>${item.unitdefinition?.uom || item.new_uom}</span>
                                        </div>
                                        `
                    : '-'
                }
                                </td>
                            </tr>
                        `;
        }).join('')}
                </tbody>
            </table>

        </body>
        </html>
    `;

        printWindow.document.write(content);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl my-2 mx-auto">
                    {token && selectedOutlet && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <div className="flex justify-between items-center mb-3">
                                <div className="text-lg font-semibold">Physical Stock Count for {selectedOutlet}</div>

                                <div>
                                    <button
                                        onClick={handlePrint}
                                        className="px-3 py-1.5 rounded-md text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md"
                                    >
                                        Print Sheet
                                    </button>
                                </div>
                            </div>

                            <div className="rounded-md border overflow-auto max-h-[650px] tiny-scrollbar">
                                <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                    <thead className="bg-gray-50 sticky top-0 z-20">
                                        <tr>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Definition</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Primary Unit</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Secondary Unit</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Cost</th>
                                        </tr>
                                        <tr>
                                            <th></th>
                                            <th></th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                            <th></th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {[...physicalItems]
                                            .sort((a, b) =>
                                                a.item_name.localeCompare(b.item_name)
                                            )
                                            .map((item, index) => {
                                                const showSecondary = shouldShowSecondary(item);

                                                return (
                                                    <tr key={`${item.item_name}-${item.uom}`}>
                                                        <td className="px-6 py-4 whitespace-nowrap">{item.item_name}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap">{item.unitdefinition?.unit || 0} ({item.unitdefinition?.uom || 'N/A'})</td>

                                                        <td className="px-6 py-4 whitespace-nowrap border-r">{item.uom}</td>
                                                        <td>
                                                            <input
                                                                ref={(el) => {
                                                                    if (el) inputRefs.current[index * 2] = el;
                                                                }}
                                                                type="number"
                                                                className="w-20 border rounded px-2 py-1"
                                                                value={item.primaryQty}
                                                                onChange={(e) => handlePrimaryChange(item.id, e.target.value)}
                                                                onKeyDown={handleKeyDown}
                                                                onBlur={(e) => {
                                                                    const updatedItem = {
                                                                        ...item,
                                                                        primaryQty: e.target.value
                                                                    };

                                                                    fetchPhysicalCost(updatedItem);
                                                                }}
                                                                step="any"
                                                                min="0"
                                                            />
                                                        </td>

                                                        <td className="px-6 py-4 whitespace-nowrap border-r">
                                                            {showSecondary ? item.new_uom : '-'}
                                                        </td>
                                                        <td>
                                                            {showSecondary ? (
                                                                <input
                                                                    ref={(el) => {
                                                                        if (el) inputRefs.current[index * 2 + 1] = el;
                                                                    }}
                                                                    type="number"
                                                                    className="w-20 border rounded px-2 py-1"
                                                                    value={item.secondaryQty}
                                                                    onChange={(e) => handleSecondaryChange(item.id, e.target.value)}
                                                                    onKeyDown={handleKeyDown}
                                                                    onBlur={(e) => {
                                                                        const updatedItem = {
                                                                            ...item,
                                                                            secondaryQty: e.target.value
                                                                        };

                                                                        fetchPhysicalCost(updatedItem);
                                                                    }}
                                                                    step="any"
                                                                    min="0"
                                                                />
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </td>

                                                        <td className="px-6 py-4">
                                                            {costLoading[item.id] ? (
                                                                <div className="flex justify-center text-blue-500">
                                                                    <ImSpinner2 className="animate-spin" size={18} />
                                                                </div>
                                                            ) : costErrors[item.id] ? (
                                                                <span
                                                                    className="text-red-500 text-xs"
                                                                    title={costErrors[item.id]}
                                                                >
                                                                    {costErrors[item.id]}
                                                                </span>
                                                            ) : (
                                                                Number(itemCosts[item.id] || 0).toFixed(2)
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex justify-end mt-3">
                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className="px-4 py-2 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md"
                                >
                                    {isLoading ? 'Saving...' : 'Save Physical Count'}
                                </button>
                            </div>

                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div >
    );
};

export default CentralKitchenPhysical;